// SPDX-License-Identifier: LicenseRef-Proprietary

import React from "react";
import { useVaccineDashboardStore } from "../../../store/vaccineDashboardStore";
import { useVaccineModelWithBackend } from "../hooks/useVaccineModelWithBackend";
import { useVaccineFiscalData } from "../hooks/useVaccineFiscalData";
import { useVaccineTabs } from "../hooks/useVaccineTabs";
import { TabPanel } from "./TabPanel";
import { PieChart } from "./PlotlyCharts";
import { EfficacyChart } from "./EfficacyChart";
import { Skeleton } from "./Skeleton";
import { BudgetImpactTimeline } from "./charts/BudgetImpactTimeline";
import { PerCycleTrace } from "./charts/PerCycleTrace";
import { ComplicationsTable } from "./charts/ComplicationsTable";
import { PaybackPeriod } from "./charts/PaybackPeriod";
import { CostComposition } from "./charts/CostComposition";
import { ScenarioWaterfall } from "./charts/ScenarioWaterfall";
import RegionalHighlights from "./charts/RegionalHighlights";
import { REGIONS } from "../lib/models";
import { getNationalPopulation } from "../../../data/regions";

/**
 * Connected wrapper component for TabPanel
 * Composes JSX structure using compound components pattern
 * Uses backend results when available, falls back to frontend calculation
 */
export const TabPanelConnected: React.FC = () => {
  const inputs = useVaccineDashboardStore((state) => state.inputs);
  const globalInputs = useVaccineDashboardStore((state) => state.globalInputs);
  const scenarios = useVaccineDashboardStore((state) => state.scenarios);
  const activeTab = useVaccineDashboardStore((state) => state.activeTab);
  const setActiveTab = useVaccineDashboardStore((state) => state.setActiveTab);
  const regionalState = useVaccineDashboardStore((state) => state.regional);
  const setSelectedRegion = useVaccineDashboardStore((state) => state.setSelectedRegion);

  const model = useVaccineModelWithBackend(inputs);
  const fiscalData = useVaccineFiscalData(model, inputs.timeHorizonYears);
  
  const tabData = useVaccineTabs({
    model,
    inputs,
    fiscalData,
    scenarios,
  });

  const selectedRegionId = regionalState.selectedRegionId || 'finland';
  const selectedRegionName =
    REGIONS.find((region) => region.key === selectedRegionId)?.name || 'Finland';

  // Find regional data from backend model
  const regionalMetrics = model?.regional?.find(r => r.id === selectedRegionId) || model?.regional?.find(r => r.id === 'finland');
  
  // Calculate "Selected Total" metrics (unscaled sum of selected regions)
  const selectedTotalMetrics = React.useMemo(() => {
    if (!model?.regional) return null;
    
    const finlandMetric = model.regional.find(r => r.id === 'finland');
    if (finlandMetric) {
        // Normalize Finland metric to match the "weighted sum" structure of the reducer below
        // The reducer produces coverage = sum(coverage_pct * population)
        return {
            ...finlandMetric,
            coverage: finlandMetric.coverage * finlandMetric.population
        };
    }

    const initialValue: typeof regionalMetrics = {
      id: 'selected_total',
      name: 'Selected Total',
      population: 0,
      coverage: 0, 
      casesPrevented: 0,
      hospitalizationsPrevented: 0,
      lifeYearsGained: 0,
      costImpact: 0,
    };

    return model.regional.reduce((acc, curr) => {
      if (!acc) return curr;
      return {
        id: 'selected_total',
        name: 'Selected Total',
        population: acc.population + curr.population,
        coverage: acc.coverage + (curr.coverage * curr.population), 
        casesPrevented: acc.casesPrevented + curr.casesPrevented,
        hospitalizationsPrevented: acc.hospitalizationsPrevented + curr.hospitalizationsPrevented,
        lifeYearsGained: acc.lifeYearsGained + curr.lifeYearsGained,
        costImpact: acc.costImpact + curr.costImpact,
      };
    }, initialValue);
  }, [model?.regional]);

  // Calculate "National" (Projected) metrics
  const nationalMetrics = React.useMemo(() => {
    if (!model?.incremental) return null;

    const FINLAND_POPULATION = getNationalPopulation(globalInputs.populationMode); // Static Real World Population (e.g. 5.6M)
    const inputPopulation = inputs.population; // User Input (e.g. 1M)

    // Scale Factor = Real World Pop / Input Pop
    const scaleFactor = inputPopulation > 0 ? FINLAND_POPULATION / inputPopulation : 1;
    
    // Get explicit National Coverage input
    const nationalCoveragePct = inputs.coveragePctByRegion['finland'] ?? inputs.defaultCoveragePct;

    return {
      id: 'national',
      name: 'National (Projected)',
      population: FINLAND_POPULATION,
      coverage: nationalCoveragePct, 
      casesPrevented: model.incremental.casesAvoided * scaleFactor,
      hospitalizationsPrevented: model.incremental.hospitalizationsAvoided * scaleFactor,
      lifeYearsGained: model.incremental.lifeYearsGained * scaleFactor,
      costImpact: model.incremental.costDifference * scaleFactor,
    };
  }, [model?.incremental, globalInputs.populationMode, inputs.population, inputs.coveragePctByRegion, inputs.defaultCoveragePct]);

  // Determine which metrics to show in the "Regional" column
  const displayedRegionalMetrics = React.useMemo(() => {
     if (selectedRegionId === 'selected_total' && selectedTotalMetrics) {
         return selectedTotalMetrics;
     }
     return regionalMetrics;
  }, [selectedRegionId, selectedTotalMetrics, regionalMetrics]);

  // Fix coverage for displayedRegionalMetrics and selectedTotalMetrics for display
  const displayReadyRegional = React.useMemo(() => {
      if (!displayedRegionalMetrics) return null;
      const metric = { ...displayedRegionalMetrics };
      
      if (metric.id === 'selected_total' && metric.population > 0) {
          // Normalize weighted sum to percentage
          metric.coverage = (metric.coverage / metric.population);
      } 
      
      // Ensure coverage is 0-100
      // If it's <= 1 (e.g. 0.75), multiply by 100. If it's > 1 (e.g. 75), leave it.
      if (metric.coverage <= 1 && metric.coverage > 0) {
          metric.coverage = metric.coverage * 100;
      }
      
      return metric;
  }, [displayedRegionalMetrics]);

  const displayReadySelectedTotal = React.useMemo(() => {
      if (!selectedTotalMetrics) return null;
      const metric = { ...selectedTotalMetrics };
      if (metric.population > 0) {
           // Normalize weighted sum to percentage
           metric.coverage = (metric.coverage / metric.population);
      }
      
      // Ensure coverage is 0-100
      if (metric.coverage <= 1 && metric.coverage > 0) {
          metric.coverage = metric.coverage * 100;
      }
      return metric;
  }, [selectedTotalMetrics]);

  const regionalData = displayReadyRegional && nationalMetrics ? {
    regional: displayReadyRegional,
    national: nationalMetrics,
    selectedTotal: displayReadySelectedTotal
  } : null;

  return (
    <TabPanel activeTab={activeTab} onTabChange={setActiveTab} variant="dropdown">
      <TabPanel.Tab tabKey="timeline" label="Timeline">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <BudgetImpactTimeline
            data={model.series}
            timeHorizon={inputs.timeHorizonYears}
            discountingEnabled={inputs.discountingEnabled ?? false}
            discountRatePct={inputs.discountRatePct ?? 3}
          />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="per-cycle" label="Per-cycle trace">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <PerCycleTrace data={model.series} />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="complications" label="Complications">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <ComplicationsTable
            series={model.series}
            complications={inputs.complications ?? []}
          />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="payback" label="Payback">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <PaybackPeriod data={tabData.timelineData} />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="efficacy" label="Efficacy">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <EfficacyChart model={model} />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="composition" label="Cost composition">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <CostComposition costBreakdown={tabData.costBreakdownData} />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="breakdown" label="Cost breakdown">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <PieChart data={tabData.breakdownData.pieData} />
        )}
      </TabPanel.Tab>

      <TabPanel.Tab tabKey="waterfall" label="Budget story">
        {!model ? (
          <Skeleton width="100%" height="400px" borderRadius="8px" />
        ) : (
          <ScenarioWaterfall
            model={model}
          />
        )}
      </TabPanel.Tab>
      
      <TabPanel.Tab tabKey="regional" label="Regional">
         {!model || !regionalData ? (
            <Skeleton width="100%" height="400px" borderRadius="8px" />
         ) : (
            <RegionalHighlights 
               selectedRegion={selectedRegionName}
               regionalMetrics={regionalData.regional}
               nationalMetrics={regionalData.national}
               totalSelectedMetrics={regionalData.selectedTotal}
               timeHorizonYears={inputs.timeHorizonYears}
               availableRegions={model.regional}
               onRegionChange={(regionId) => setSelectedRegion(regionId)}
            />
         )}
      </TabPanel.Tab>
    </TabPanel>
  );
};
